import React, { useState, useEffect } from 'react';
import { syncService } from '../services/syncService';

/**
 * Offline status indicator component
 * Shows online/offline status and sync progress
 */
const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState({ syncing: false, progress: 0 });
  const [pendingChanges, setPendingChanges] = useState(0);

  useEffect(() => {
    // Subscribe to network status changes
    const unsubscribeNetwork = syncService.onNetworkStatusChange((online) => {
      setIsOnline(online);

      // Update pending changes count when status changes
      if (online) {
        updatePendingChanges();
      }
    });

    // Subscribe to sync status changes
    const unsubscribeSync = syncService.onSyncStatusChange((status) => {
      setSyncStatus(status);

      // Update pending changes count after sync completes
      if (status.syncing === false) {
        updatePendingChanges();
      }
    });

    // Update pending changes count initially
    updatePendingChanges();

    // Poll for pending changes every 5 seconds (in case something changed)
    const interval = setInterval(updatePendingChanges, 5000);

    return () => {
      unsubscribeNetwork();
      unsubscribeSync();
      clearInterval(interval);
    };
  }, []);

  const updatePendingChanges = async () => {
    try {
      const stats = await syncService.getSyncStats();
      setPendingChanges(stats.pendingChanges);
    } catch (error) {
      console.error('Error updating pending changes:', error);
    }
  };

  const handleSyncNow = async () => {
    try {
      await syncService.syncPendingChanges();
      // Update count after sync
      await updatePendingChanges();
    } catch (error) {
      console.error('Error syncing:', error);
    }
  };

  // Only show when offline or syncing (hide when online and idle)
  if (isOnline && !syncStatus.syncing) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div
        className="rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 bg-gray-800 text-white transition-all duration-300"
      >
        {/* Status icon */}
        <div className="flex-shrink-0">
          {syncStatus.syncing ? (
            <svg
              className="animate-spin h-5 w-5 text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <svg
              className="h-5 w-5 text-yellow-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
              />
            </svg>
          )}
        </div>

        {/* Status text */}
        <div className="flex-1 min-w-0">
          {syncStatus.syncing ? (
            <div>
              <p className="text-sm font-medium">Syncing...</p>
              {syncStatus.progress > 0 && (
                <div className="mt-1 w-full bg-gray-600 rounded-full h-1.5">
                  <div
                    className="bg-blue-400 h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${syncStatus.progress}%` }}
                  />
                </div>
              )}
            </div>
          ) : (
            <div>
              <p className="text-sm font-medium">Offline Mode</p>
              <p className="text-xs opacity-80">Changes will sync when online</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OfflineIndicator;
